/**
 * The popup action menu for a single timeline event — used by
 * {@link CaptureTimeline} in `actionMenu` / `contextMenu` mode and reused
 * by the Activity feed's kebab. The item conditions mirror the inline
 * icon column and {@link hasAnyAction} exactly (all three read the same
 * handler set) so the surfaces can't drift.
 */
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SellIcon from '@mui/icons-material/Sell';
import TimelineIcon from '@mui/icons-material/Timeline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import type { TimelineActionHandlers, TimelineEvent } from './helpers';

/** MenuItems for one event in actionMenu mode — mirrors the inline
 *  icon conditions (Download / Attach / Enable-Disable / Play). Exported so
 *  the Activity feed can offer the identical action set from its own kebab
 *  menu without re-deriving the conditions. */
export function buildMenuItems(
  event: TimelineEvent,
  h: TimelineActionHandlers & { close: () => void },
) {
  const isSession = event.kind === 'session';
  const isDeleted = event.deletedAt !== null;
  const noRecording = isSession && event.s3Key === null;
  const items = [];
  if (h.onDownload && !(h.hideSessionDownload && isSession)) {
    items.push(
      <MenuItem
        key="download"
        disabled={noRecording}
        onClick={() => {
          h.onDownload!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <DownloadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          {isSession ? 'Download recording' : 'Download baseline'}
        </ListItemText>
      </MenuItem>,
    );
  }
  if (h.onAttach && isSession) {
    items.push(
      <MenuItem
        key="attach"
        onClick={() => {
          h.onAttach!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <AttachFileIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Attach video or audio</ListItemText>
      </MenuItem>,
    );
  }
  if (h.onTag && isSession) {
    items.push(
      <MenuItem
        key="tag"
        onClick={() => {
          h.onTag!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <SellIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          {event.departmentId || event.locationId || event.sessionKind
            ? 'Edit department / location'
            : 'Set department / location'}
        </ListItemText>
      </MenuItem>,
    );
  }
  if (isDeleted && h.onRestore) {
    items.push(
      <MenuItem
        key="restore"
        onClick={() => {
          h.onRestore!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <VisibilityIcon fontSize="small" sx={{ color: 'success.main' }} />
        </ListItemIcon>
        <ListItemText>Re-enable</ListItemText>
      </MenuItem>,
    );
  }
  if (!isDeleted && h.onDisable) {
    items.push(
      <MenuItem
        key="disable"
        onClick={() => {
          h.onDisable!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <VisibilityOffIcon fontSize="small" sx={{ color: 'error.main' }} />
        </ListItemIcon>
        <ListItemText>Disable</ListItemText>
      </MenuItem>,
    );
  }
  if (h.onReplay && isSession && !isDeleted) {
    items.push(
      <MenuItem
        key="replay"
        onClick={() => {
          h.onReplay!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />
        </ListItemIcon>
        <ListItemText>Replay session</ListItemText>
      </MenuItem>,
    );
  }
  if (h.onSplit && isSession && !isDeleted && !noRecording) {
    items.push(
      <MenuItem
        key="split"
        onClick={() => {
          h.onSplit!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <ContentCutIcon fontSize="small" sx={{ color: 'warning.main' }} />
        </ListItemIcon>
        <ListItemText>Split into sessions</ListItemText>
      </MenuItem>,
    );
  }
  if (h.onViewTrace && isSession) {
    items.push(
      <MenuItem
        key="trace"
        onClick={() => {
          h.onViewTrace!(event);
          h.close();
        }}
      >
        <ListItemIcon>
          <TimelineIcon fontSize="small" sx={{ color: 'primary.main' }} />
        </ListItemIcon>
        <ListItemText>View performance trace</ListItemText>
      </MenuItem>,
    );
  }
  return items;
}
